import React, { useContext, useEffect, useState } from "react";
import ConfirmationHeader from "./ConfirmationHeader";
import OrderDetails from "../common/OrderDetails";

import { buyNowContext } from "../BuyNow";
import trackingScripts from "../../utils/trackingScripts";
import { createOrderObject, FACEBOOK_EVENTS } from "../../utils/facebookHelper";
import {PORTALS, PRODUCT_CATEGORIES, PURCHASE_FLOWS, SIGNUP_USER_TYPES} from "../../../../constants";
import ConfirmationMessage from "./ConfirmationMessage";
import CreateAccountForm from "./CreateAccountForm";
import CreateClearStepClientAccount from "./CreateClearStepClientAccount";

const Confirmation = () => {
  const {
    PURCHASE_STAGES, shippingStageState, order, lineItems, isProduction, giftSenderInformation,
    portal, purchaseFlow, appInfo
  } = useContext(buyNowContext);
  const businessName = appInfo.name;
  const URLS = {
    "Shapa": '/home',
    "MyClearStep": 'https://www.myclearstep.com',
  };

  const scaleLineItem = lineItems.find(li => li.product.category === PRODUCT_CATEGORIES.SCALE);
  const planLineItem = lineItems.find(li => li.product.category === PRODUCT_CATEGORIES.MEMBERSHIP);
  const selectedScale = scaleLineItem ? scaleLineItem.product : null;
  const selectedPlan = planLineItem ? planLineItem.product : null;

  const [user, setUser] = useState(null);
  const [confirmationStage, setConfirmationStage] = useState(1);
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  const fbTrackingOrderObject = createOrderObject(selectedScale, selectedPlan);

  useEffect(() => {
    history.replaceState({ stage: PURCHASE_STAGES.CONFIRMATION }, null, "");

    // Remove the local storage item we used to keep track of how the user was referred to our site.
    localStorage.removeItem("shapa_referrer");

    trackingScripts.googleECommerce(isProduction, order);
    trackingScripts.hotjar(isProduction, "/orders/buy-now/purchase_confirmation");
    trackingScripts.facebook(isProduction, FACEBOOK_EVENTS.REACT_PAGE_VIEW, { page: "PurchaseConfirmation" }, "trackCustom");
    trackingScripts.facebook(isProduction, FACEBOOK_EVENTS.PURCHASE, fbTrackingOrderObject, "track", shippingStageState.email);
    trackingScripts.googleConversionGTag(isProduction);
    trackingScripts.sendGoogleAnalyticsEvent(isProduction, "purchase", "ecommerce", "Purchase");
  }, []);

  useEffect(() => {
    if (giftSenderInformation.isGift) {
      setConfirmationStage(2);
    }
  }, []);

  const scrollToCreateAccount = () => {
    document.getElementById('create-from').scrollIntoView({behavior: "smooth", block: "center", inline: "nearest"});
  };

  const postOrderPurchase = () => (
    <div className={confirmationStage === 2 ? "mb-lg-5 pb-lg-5" : "" + "container-fluid"} style={{ marginTop: "110px" }}>
      <div className="row">
        <div className="offset-lg-1 col-lg-6 text-center">
          <div className="row mb-2">
            <div className="offset-lg-2 col-lg-6 justify-content-center">
              <i className="success-teal-circle mb-2 fa fa-check-circle"></i>
            </div>
            {confirmationStage === 1 && <ConfirmationMessage />}
            {confirmationStage === 2 && <div className="offset-2 col-8 offset-lg-2 col-lg-6">
              <p className="text-dark header-txt">Thank you for your order</p>
              <p className="next-button-txt text-muted">We've emailed you order confirmation details. Your Shapa will arrive within 5-7 days.</p>
              <p className="next-button-txt text-muted mb-lg-4">
                We have also sent a gift confirmation email that includes important steps to get started and a unique license key that your recipient will need to create an account.
              </p>
            </div>}
            {confirmationStage === 2 && <div className="offset-lg-2 col-lg-6 d-flex justify-content-center">
              <a className="teal button-link next-button-txt next-step-button lg-100-sm-75 text-center px-lg-3 mb-3 mt-3 mt-lg-0" href="/">
                Back to Shapa homepage
              </a>
            </div>}
          </div>
          {confirmationStage === 1 &&
            <React.Fragment>
              {/* The various purchase flows have a slightly different account creation flow. */}

              {(purchaseFlow === PURCHASE_FLOWS.SHAPA_END_USER || purchaseFlow === PURCHASE_FLOWS.SHAPA_HEALTH_END_USER) &&
                <React.Fragment>
                  {/* This button will scroll down to the create form when clicked. */}
                  <div className="offset-lg-2 col-lg-6 d-flex flex-column">
                    <a className="teal button-link next-button-txt next-step-button text-center px-lg-3 mb-3 align-self-center lg-100-sm-75" onClick={() => scrollToCreateAccount()}>
                      Create my account
                    </a>
                  </div>
                  <CreateAccountForm onAccountCreated={userResponse => setUser(userResponse)} appName={businessName}/>
                </React.Fragment>
              }
              {(purchaseFlow === PURCHASE_FLOWS.SHAPA_BP_ADDON || purchaseFlow === PURCHASE_FLOWS.CLEARSTEP_BP_ADDON) && (
                <div className="offset-lg-2 col-lg-6 d-flex flex-column">
                  <a href={URLS[businessName]} className="teal button-link next-button-txt next-step-button text-center px-lg-3 mb-3 align-self-center lg-100-sm-75">
                    Back to home
                  </a>
                </div>
              )}
              {(purchaseFlow === PURCHASE_FLOWS.CLEARSTEP_END_USER) && <CreateClearStepClientAccount onAccountCreated={userResponse => setUser(userResponse)} appName={businessName}/>}
              {(purchaseFlow === PURCHASE_FLOWS.CLEARSTEP_CLINICIAN || purchaseFlow === PURCHASE_FLOWS.CLEARSTEP_PARENT) &&
                <div className="offset-lg-2 col-lg-6 d-flex flex-column">
                  <a href="https://www.myclearstep.com/" className="teal button-link next-button-txt next-step-button text-center px-lg-3 mb-3 align-self-center lg-100-sm-75">
                    Back to home
                  </a>
                </div>
              }
            </React.Fragment>
          }
        </div>
        <OrderDetails
          showOrderDetails={showOrderDetails}
        />
      </div>

      { // Share-a-Sale tracking pixel.
        isProduction &&
        <img src={`https://shareasale.com/sale.cfm?amount=${order.totalPrice}&tracking=${order.id}&transtype=sale&merchantID=76294`} width="1" height="1"/>
      }
    </div>
  );

  const postAccountCreation = () => (
    <div style={{ textAlign: "center", paddingTop: "20px" }}>
      <i className="success-teal-circle mb-2 fa fa-check-circle" style={{ fontSize: "100px" }}></i>

      <h2 className="header-txt text-dark fw-bold">You are now registered with {businessName}</h2>

      {/* Tell the user to download the app unless they have purchased for a child/dependent. */}
      {user.signupUserType !== SIGNUP_USER_TYPES.PARENT &&
        <React.Fragment>
          <div className="description-txt">Get started by downloading the {businessName} app!</div>

          <div>
            <a href={appInfo.iosStoreUrl} target="_blank">
              <img src="https://www.myshapa.com/img/App_Store_Badge.png" style={{ height: "50px", marginRight: "5px", marginTop: "25px" }}/>
            </a>

            <a href={appInfo.androidStoreUrl} target="_blank">
              <img src="https://www.myshapa.com/img/Google_Play_Badge.png" style={{ height: "50px", marginTop: "25px" }}/>
            </a>
          </div>
        </React.Fragment>
      }

      <div style={{ padding: "0 25px" }}>
        <div style={{ borderRadius: "10px", boxShadow: "0 0 0.5em rgb(7 3 0 / 30%)", display: "inline-block", width: "100%", maxWidth: "500px", textAlign: "left", marginTop: "50px", padding: "0 50px 25px 50px" }}>
          <h2 className="header-txt text-dark fw-bold">Your account details</h2>

          <div className="description-txt">First Name: {user.firstName}</div>
          <div className="description-txt">Last Name: {user.lastName}</div>
          <div className="description-txt">Email: {user.email}</div>
          {user.phone && <div className="description-txt">Phone: {user.phone}</div>}

          <div className="description-txt" style={{marginTop: "25px", textAlign: "center"}}>
            {portal === PORTALS.SHAPA &&
              <a
                className="teal button-link next-button-txt next-step-button text-center px-lg-3 mb-3 align-self-center lg-100-sm-75"
                href="/users"
                target="_blank"
              >
                Get Started!
              </a>
            }

            {(portal === PORTALS.CLEARSTEP || portal === PORTALS.SHAPA_HEALTH) &&
              <a
                style={{ color: "var(--main-color)", textDecoration: "underline" }}
                href="/users"
                target="_blank"
              >
                Go to your account
              </a>
            }
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <React.Fragment>
      {/*
          Need a better solution here. When an account has been created, we no longer render the OrderDetails component, so it won't be shown when the dropdown is clicked.
          The best solution is to use two OrderDetails components. One for the dropdown and one for the desktop view. Why are we only using one component?
          Another solution is to show the regular header and don't render the ConfirmationHeader once a user account is created.
       */}
      <ConfirmationHeader displayOrderDetailsDropdown={!user} setShowOrderDetails={setShowOrderDetails} showOrderDetails={showOrderDetails}/>

      {/* We'll first show the order confirmation page. If the user optionally creates an account, we'll then show an account confirmation page. */}
      {user ? postAccountCreation() : postOrderPurchase()}
    </React.Fragment>
  );
};

export default Confirmation;
